import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { DRIVER_FACE_PHOTO_MAX } from '@cobliteam/ui-business/components/drawers/DriverDrawer/constants';
import { format } from '@cobliteam/utils/date';
import { type KeyboardEvent, useState } from 'react';
import type { PhotoCluster } from '../types';

interface PhotoClusterCardProps {
  cluster: PhotoCluster;
  index: number; // 1-based position within current page for aria-label
  total: number; // total clusters in page for aria-label
  selected: boolean;
  disabled?: boolean; // true when cap reached and this card is not selected
  onToggle: () => void;
}

const formatCaption = (date: Date) => format(date, 'dd/MM HH:mm:ss');

export const PhotoClusterCard = ({ cluster, index, total, selected, disabled, onToggle }: PhotoClusterCardProps) => {
  const { t } = useTranslation('drivers');
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const photoCount = cluster.photos.length;
  const photo = cluster.photos[activePhotoIdx];
  const canPrev = activePhotoIdx > 0;
  const canNext = activePhotoIdx < photoCount - 1;

  if (!photo) return null;

  const handlePrev = () => {
    if (canPrev) {
      setActivePhotoIdx((i) => i - 1);
      setImgError(false);
      setImgLoaded(false);
    }
  };
  const handleNext = () => {
    if (canNext) {
      setActivePhotoIdx((i) => i + 1);
      setImgError(false);
      setImgLoaded(false);
    }
  };

  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onToggle();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      handlePrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      handleNext();
    }
  };

  const stateLabel = selected ? t('face_id.selector.card.state_selected') : t('face_id.selector.card.state_unselected');
  const ariaLabel = t('face_id.selector.card.aria_label', {
    current: index,
    total,
    photoCurrent: activePhotoIdx + 1,
    photoTotal: photoCount,
    stateLabel,
  });

  const caption = formatCaption(new Date(photo.capturedAt));
  const hasCoords = typeof photo.latitude === 'number' && typeof photo.longitude === 'number';
  const lat = hasCoords ? photo.latitude!.toFixed(4) : '';
  const lon = hasCoords ? photo.longitude!.toFixed(4) : '';

  return (
    <Tooltip content={t('face_id.selector.card.disabled_over_cap', { max: DRIVER_FACE_PHOTO_MAX })} enable={Boolean(disabled)}>
      {/* biome-ignore lint/a11y/useSemanticElements: div with role=button is required because the card contains nested <button> arrow controls (nested buttons are invalid HTML) */}
      <div
        role='button'
        tabIndex={disabled ? -1 : 0}
        aria-pressed={selected}
        aria-disabled={disabled}
        aria-label={ariaLabel}
        onClick={disabled ? undefined : onToggle}
        onKeyDown={handleKey}
        className={cn(
          'group relative size-[200px] overflow-hidden rounded-lg border-2 outline-none',
          'focus-visible:ring-2 focus-visible:ring-primary-7 focus-visible:ring-offset-2',
          selected ? 'border-primary-9' : 'border-transparent',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        {imgError ? (
          <div className='flex size-full flex-col items-center justify-center gap-2 bg-gray-3 text-gray-10'>
            <Icon name='camera-off' />
            <span className='text-caption'>{t('face_id.selector.card.unavailable')}</span>
          </div>
        ) : (
          <img
            src={photo.imageUrl}
            alt=''
            loading='lazy'
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
            className={cn('size-full object-cover transition-opacity duration-150', imgLoaded ? 'opacity-100' : 'opacity-0')}
          />
        )}
        {!(imgLoaded || imgError) && <div className='absolute inset-0 animate-pulse bg-gray-3' />}

        <div className='pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-black/40 to-transparent' aria-hidden />
        <div className='pointer-events-none absolute top-3 left-3' aria-hidden>
          <Checkbox checked={selected} disabled={disabled} tabIndex={-1} className={cn(!selected && 'bg-white')} />
        </div>

        <Button
          variant='outlined'
          color='default'
          size='sm'
          aria-label={t('face_id.selector.card.prev')}
          disabled={!canPrev}
          onClick={(e) => {
            e.stopPropagation();
            handlePrev();
          }}
          className='-translate-y-1/2 absolute top-1/2 left-2 size-6 rounded-full border-none bg-black/30 text-white opacity-0 shadow-sm transition-opacity hover:bg-black/50 disabled:cursor-not-allowed disabled:bg-black/30 disabled:text-gray/50 group-focus-within:opacity-100 group-hover:opacity-100'
          icon={<Icon name='chevron-left' size='sm' />}
        />
        <Button
          variant='outlined'
          color='default'
          size='sm'
          aria-label={t('face_id.selector.card.next')}
          disabled={!canNext}
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          className='-translate-y-1/2 absolute top-1/2 right-2 size-6 rounded-full border-none bg-black/30 text-white opacity-0 shadow-sm transition-opacity hover:bg-black/50 disabled:cursor-not-allowed disabled:bg-black/30 disabled:text-gray/50 group-focus-within:opacity-100 group-hover:opacity-100'
          icon={<Icon name='chevron-right' size='sm' />}
        />

        <Typography variant='text-sm-medium' color='white' className='absolute top-2 right-2 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100'>
          {t('face_id.selector.card.photo_index', { current: activePhotoIdx + 1, total: photoCount })}
        </Typography>

        <div className='pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/70 via-black/40 to-transparent' aria-hidden />
        <div className='pointer-events-none absolute right-2 bottom-2 left-2 font-mono text-[10px] text-white leading-tight'>
          <div>{caption}</div>
          {hasCoords && <div>{`${lat} ${lon}`}</div>}
        </div>
      </div>
    </Tooltip>
  );
};

export default PhotoClusterCard;
